import { useEffect, useState } from 'react';
import { FiUserCheck, FiFilter } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import { FaPlus, FaPen, FaTrash } from 'react-icons/fa6';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import BeneficiaryFormModal from './shared/BeneficiaryFormModal';
import BeneficiaryContact from './shared/BeneficiaryContact';
import { maskDocument } from '../credit-analysis/utils/documentUtils';
import ConfirmDialog from './shared/ConfirmDialog';
import FilterPanel from './shared/FilterPanel';
import ActiveFilterChips from './shared/ActiveFilterChips';
import { BENEFICIARY_FILTER_DEFS } from './shared/expenseFilterTypes';
import { deriveActiveFilters } from './shared/filterTypes';
import useBeneficiaries from '../../../hooks/useBeneficiaries';
import { recoveryApi } from '../../../services/recoveryApi';
import { BENEFICIARY_TYPE_OPTIONS, type Beneficiary, type SelectOption } from '../../../types/LegalCase';

const columns = [
    { id: 'name', title: 'Nome', field: 'name', canSort: false, width: '280px' },
    { id: 'beneficiaryType', title: 'Tipo', field: 'beneficiaryType', canSort: false, width: '200px' },
    { id: 'document', title: 'Documento', field: 'document', canSort: false, width: '180px' },
    { id: 'actions', title: '', field: 'actions', canSort: false, width: '90px', cssClass: 'text-center' },
];

// Fallback dos tipos: fonte única em types/LegalCase (sem IEPTB/INPI — AF-3691). Não
// redeclarar aqui, senão a restrição fica incompleta se o page-form falhar (apontamento Copilot).
const TYPE_FALLBACK: SelectOption[] = BENEFICIARY_TYPE_OPTIONS;

// Página top-level do registro global de Beneficiários (Jurídico → Beneficiários). Lista com
// busca e filtro por tipo, cadastro/edição por modal e remoção com confirmação (409 quando há
// despesa vinculada — a mensagem do backend aparece no toast).
const BeneficiaryPageList = () => {
    const b = useBeneficiaries();
    const [typeOptions, setTypeOptions] = useState<SelectOption[]>(TYPE_FALLBACK);
    const [searchInput, setSearchInput] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState<Beneficiary | null>(null);
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const activeFilters = deriveActiveFilters(BENEFICIARY_FILTER_DEFS, b.filterValues);

    useEffect(() => {
        recoveryApi.getLegalCaseFormOptions()
            .then((opts) => { if (opts?.beneficiaryTypes?.length) setTypeOptions(opts.beneficiaryTypes); })
            .catch(() => { /* fallback local */ });
    }, []);

    useEffect(() => {
        if (!b.isLoading) setInitialLoading(false);
    }, [b.isLoading]);

    const openCreate = () => {
        b.clearErrors();
        setEditing(null);
        setShowForm(true);
    };

    const openEdit = (beneficiary: Beneficiary) => {
        b.clearErrors();
        setEditing(beneficiary);
        setShowForm(true);
    };

    const submitForm = async (data: {
        name: string; beneficiaryType: string; document?: string; personId?: string;
        email?: string; phone?: string; mobile?: string; description?: string;
    }): Promise<boolean> => {
        const ok = editing
            ? await b.updateBeneficiary(editing.id, data)
            : !!(await b.addBeneficiary(data));
        if (ok) setShowForm(false);
        return ok;
    };

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        const ok = await b.removeBeneficiary(pendingRemoval);
        if (ok) setPendingRemoval(null);
    };

    return (
        <>
            {b.isLoading && <Loading />}

            <div className="flex flex-col h-screen bg-white dark:bg-slate-800 overflow-hidden">
                <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <FiUserCheck className="text-blue-600" size={20} />
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Beneficiários</h1>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-56">
                                <TextEdit
                                    label="Buscar nome, documento..."
                                    value={searchInput}
                                    onChange={(e: any) => setSearchInput(e.target.value)}
                                    onKeyDown={(e: any) => { if (e.key === 'Enter') b.setSearch(searchInput); }}
                                />
                            </div>
                            <button onClick={() => setFiltersOpen(true)}
                                className="relative flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-sm font-medium"
                                title="Filtros">
                                <FiFilter size={14} /> Filtros
                                {activeFilters.length > 0 && (
                                    <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-blue-600 text-white">
                                        {activeFilters.length}
                                    </span>
                                )}
                            </button>
                            <button onClick={b.refresh}
                                className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                title="Atualizar lista">
                                <IoIosRefresh size={18} />
                            </button>
                            <button onClick={openCreate}
                                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                                <FaPlus size={12} /> Novo
                            </button>
                        </div>
                    </div>
                </div>

                <ActiveFilterChips
                    filters={activeFilters}
                    onRemove={(id) => b.setFilterValue(id, '')}
                    onClearAll={b.clearFilters}
                />

                <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                    {initialLoading ? (
                        <TableSkeleton rows={12} />
                    ) : (
                        <ATable headers={columns}>
                            {b.beneficiaries.length === 0 ? (
                                <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhum beneficiário cadastrado</td></tr>
                            ) : b.beneficiaries.map((row: Beneficiary, index: number) => (
                                <tr key={row.id}
                                    className={`group ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white align-top">
                                        <div className="truncate" title={row.name}>{row.name}</div>
                                        <BeneficiaryContact beneficiary={{ ...row, document: null }} />
                                    </td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300 align-top">{row.beneficiaryTypeFormatted}</td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-gray-500 dark:text-gray-400 align-top">{row.document ? maskDocument(row.document) : '—'}</td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center align-top">
                                        <div className="flex items-center justify-center gap-1">
                                            <button type="button" onClick={() => openEdit(row)} title="Editar"
                                                className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg">
                                                <FaPen size={11} />
                                            </button>
                                            <button type="button" onClick={() => setPendingRemoval(row.id)} title="Remover"
                                                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg">
                                                <FaTrash size={11} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </ATable>
                    )}
                </div>
            </div>

            <FilterPanel
                open={filtersOpen}
                defs={BENEFICIARY_FILTER_DEFS}
                values={b.filterValues}
                onChange={b.setFilterValue}
                onClearAll={b.clearFilters}
                onClose={() => setFiltersOpen(false)}
            />

            {showForm && (
                <BeneficiaryFormModal
                    beneficiary={editing}
                    typeOptions={typeOptions}
                    errorFields={b.errorFields}
                    isSaving={b.isSaving}
                    onSubmit={submitForm}
                    onCancel={() => setShowForm(false)}
                />
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover beneficiário"
                message="Remover este beneficiário? Não é possível se houver despesa vinculada."
                isBusy={b.isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </>
    );
};

export default BeneficiaryPageList;
